/* eslint-disable react/no-danger */
import { MutationFunctionOptions, useMutation, useQuery } from '@apollo/client';
import { useMemo, useState } from 'react';
import ErrorPage from '@website/components/utilities/ErrorPage';
import LoadingPage from '@website/components/utilities/LoadingPage';
import {
  EditTripFaQsDocument,
  EditTripFaQsQuery,
  EditTripFaqDocument,
  EditTripFaqMutation,
  UpdateTripFaqInput,
  DeleteTripFaqDocument,
  TripFaQsCategories,
} from '@website/types/graphqlOperations';
import { LegacyTrip } from '@website/types/LegacyTrip';
import { Button, Card, Form, InputGroup } from 'react-bootstrap';
import { BsFillCaretRightFill, BsFillCaretDownFill } from 'react-icons/bs';
import { useFieldArray, useForm } from 'react-hook-form';
import { SubmitButton } from '@website/components/forms/fields/SubmitButton';
import OfflineAlert from '@website/components/utilities/OfflineAlert';
import urlRegexSafe from 'url-regex-safe';
import ReactPlayer from 'react-player';

/**
 * FAQ submit handler
 */
export async function faqSubmitHandler(
  values: UpdateTripFaqInput,
  updateFAQMutation: (
    options?: MutationFunctionOptions<
      EditTripFaqMutation,
      {
        input: UpdateTripFaqInput;
      }
    >,
  ) => Promise<any>,
  setOpen: (v: boolean) => void,
  currentReadCount?: number,
) {
  // Remove any trailing question marks from the question
  const input: UpdateTripFaqInput = {
    id: values.id,
    question: values.question.replace(/[ ]*\?*[ ]*$/, ''),
    answer: values.answer,
    tripID: values.tripID,
    videos: values.videos,
    links: values.links,
    category: values.category,
  };

  // Optimistically close the FAQ
  setOpen(false);

  // Update
  await updateFAQMutation({
    variables: {
      input,
    },
    // Set the optimistic response
    optimisticResponse: {
      updateTripFAQ: {
        // Set a default ID (for new FAQs) - Apollo will replace this once the response comes in.
        id: `faq_${Date.now()}`,
        readCount: 0 || currentReadCount,
        ...values,
      },
    },
    onError: (error) =>
      alert(
        `Sorry - updating the FAQ failed. Please try again. \n\nThe full error message was: \n\n${error}`,
      ),
  });
}

/**
 * FAQ Links
 *
 * Allows hosts to add links/buttons to the FAQs
 */
export function LinksFields({ register, control, errors }) {
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'links', // unique name for your Field Array
  });

  return (
    <div className="mt-3 mb-3">
      <Form.Label>Links</Form.Label>
      <p className="text-muted small">
        Links will only show up on the user dashboard (as buttons) - they will
        not show on the trip page. This means you should use them for extra
        information, but make sure the question is still answered above.
      </p>
      {fields.map((field, index) => (
        <Form.Group key={field.id}>
          <Form.Label htmlFor={`links.${index}.title`}>
            Link {index + 1} title
          </Form.Label>
          <Form.Control {...register(`links.${index}.title`)} required />
          <Form.Label htmlFor={`links.${index}.url`}>
            Link {index + 1} url (website address)
          </Form.Label>

          <Form.Control
            required
            {...register(`links.${index}.url`, {
              pattern: urlRegexSafe(),
              setValueAs: (urlWithoutTrim) => {
                const url = urlWithoutTrim.trim();
                if (url.includes('http://') || url.includes('https://')) {
                  return url;
                }
                return `http://${url}`;
              },
            })}
            isInvalid={errors?.links?.[index]?.url}
          />

          {errors?.links?.[index]?.url && (
            <Form.Control.Feedback type="invalid">
              Must be a valid web address.
            </Form.Control.Feedback>
          )}

          <Button
            variant="danger"
            size="sm"
            className="mt-2 mb-1"
            onClick={() => remove(index)}
          >
            - Remove link
          </Button>
        </Form.Group>
      ))}
      <Button
        onClick={() =>
          append({
            title: '',
            url: '',
          })
        }
        size="sm"
        variant="muted"
        className="my-1"
      >
        + Add link
      </Button>
    </div>
  );
}

export function VideoFields({ register, control, errors }) {
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'videos',
  });

  return (
    <div className="mt-2 mb-5">
      <Form.Label>Videos</Form.Label>
      <p className="text-muted small">
        Videos will only show up on the user dashboard - they will not show on
        the trip page.
      </p>
      {fields.map((field, index) => (
        <Form.Group key={field.id}>
          <Form.Label htmlFor={`videos.${index}.url`}>
            Video {index + 1} url (e.g. youtube link)
          </Form.Label>

          <Form.Control
            required
            {...register(`videos.${index}.url`, {
              validate: (url) => ReactPlayer.canPlay(url),
              setValueAs: (urlWithoutTrim) => {
                const url = urlWithoutTrim.trim();
                if (url.includes('http://') || url.includes('https://')) {
                  return url;
                }
                return `http://${url}`;
              },
            })}
            isInvalid={errors?.videos?.[index]?.url}
          />

          {errors?.videos?.[index]?.url && (
            <Form.Control.Feedback type="invalid">
              Must be a valid youtube or vimeo url.
            </Form.Control.Feedback>
          )}

          <Button
            variant="danger"
            size="sm"
            className="mt-2 mb-1"
            onClick={() => remove(index)}
          >
            - Remove video
          </Button>
        </Form.Group>
      ))}
      <Button
        onClick={() =>
          append({
            url: '',
          })
        }
        size="sm"
        variant="muted"
        className="my-1"
      >
        + Add video
      </Button>
    </div>
  );
}

/**
 * Edit Individual FAQ Form
 */
export function FAQEditForm({
  originalFAQ,
  tripID,
  setOpen,
}: {
  originalFAQ?: EditTripFaQsQuery['trip']['faqs']['items'][0];
  tripID: string;
  setOpen: (v: boolean) => void;
}) {
  const [updateFAQMutation] = useMutation(EditTripFaqDocument, {
    update(cache, { data }) {
      // If a new FAQ, add it to the Trip cache as well (otherwise it will already be there and the normalized cache
      // will keep it in sync).
      if (!originalFAQ) {
        const tripCacheID = cache.identify({
          __typename: 'Trip',
          id: tripID,
        });

        const faqCacheID = cache.identify({
          __typename: 'TripFAQ',
          id: data.updateTripFAQ.id,
        });

        // Update the cache
        cache.modify({
          id: tripCacheID,
          fields: {
            faqs(cachedFAQs) {
              return { items: [{ __ref: faqCacheID }, ...cachedFAQs.items] };
            },
          },
        });
      }
    },
  });

  const [deleteFAQMutation] = useMutation(DeleteTripFaqDocument, {
    // Delete the FAQ from the cache
    update(cache) {
      const cacheID = cache.identify({
        __typename: 'TripFAQ',
        id: originalFAQ.id,
      });
      cache.evict({ id: cacheID });
    },
  });

  const defaultAnswer = useMemo(() => {
    try {
      const { blocks } = JSON.parse(originalFAQ?.answer);

      return blocks
        .map((block) => {
          let text = block.text || '\n';

          // Handle bullets
          if (
            block.type === 'unordered-list-item' ||
            block.type === 'ordered-list-item'
          ) {
            text = `- ${text}`;
          }

          return text;
        })
        .join('\n');
    } catch (_e) {
      // If it fails, we already have the FAQ in standard text format so use that instead!
      return originalFAQ?.answer || '';
    }
  }, [originalFAQ?.answer]);

  const {
    handleSubmit,
    register,
    control,
    formState: { isSubmitting, errors },
  } = useForm<UpdateTripFaqInput>({
    defaultValues: {
      question: '',
      links: [],
      videos: [],
      ...(originalFAQ || {}),
      tripID,
      answer: defaultAnswer,
      category: originalFAQ?.category || TripFaQsCategories.OTHERS,
    },
  });

  return (
    <>
      <OfflineAlert message="We recommend you only edit the FAQs when online, but we'll try to save the changes here when you next get a connection. Changes made here may over-write other changes." />
      <Form
        onSubmit={handleSubmit((v) =>
          faqSubmitHandler(
            v,
            updateFAQMutation,
            setOpen,
            originalFAQ?.readCount,
          ),
        )}
      >
        <Form.Group controlId="question" className="mb-3">
          <Form.Label>Question</Form.Label>
          <InputGroup>
            <Form.Control {...register('question')} required />{' '}
            <InputGroup.Text>?</InputGroup.Text>
          </InputGroup>
        </Form.Group>

        <Form.Group controlId="answer">
          <Form.Label>Answer</Form.Label>
          <Form.Control
            as="textarea"
            rows={5}
            {...register('answer')}
            required
          />
        </Form.Group>

        <Form.Group controlId="category">
          <Form.Label>Category</Form.Label>
          <Form.Select {...register('category')}>
            <option value={TripFaQsCategories.BEFORE_YOUR_TRIP}>
              Before the Trip
            </option>
            <option value={TripFaQsCategories.DURING_YOUR_TRIP}>
              During the Trip
            </option>
            <option value={TripFaQsCategories.OTHERS}>Others</option>
          </Form.Select>
        </Form.Group>

        <LinksFields register={register} control={control} errors={errors} />

        <VideoFields register={register} control={control} errors={errors} />

        <div className="mt-2">
          <SubmitButton isSubmitting={isSubmitting} divClassName="d-inline" />
          {originalFAQ && (
            <Button
              variant="danger"
              className="ms-2"
              onClick={() =>
                deleteFAQMutation({ variables: { id: originalFAQ.id } })
              }
            >
              Delete
            </Button>
          )}
        </div>
      </Form>
    </>
  );
}

/**
 * FAQ Card
 */
export function FAQCard({
  faq,
  tripID,
}: {
  faq: EditTripFaQsQuery['trip']['faqs']['items'][0];
  tripID: string;
}) {
  const [isOpen, setOpen] = useState(false);

  return (
    <Card className="mb-2" role="listitem">
      <Card.Header onClick={() => setOpen(!isOpen)}>
        {isOpen ? (
          <BsFillCaretDownFill className="me-2" />
        ) : (
          <BsFillCaretRightFill className="me-2" />
        )}
        {`${faq.question}?`}
      </Card.Header>
      {isOpen && (
        <Card.Body>
          <FAQEditForm originalFAQ={faq} tripID={tripID} setOpen={setOpen} />
        </Card.Body>
      )}
    </Card>
  );
}

/**
 * List of FAQ cards
 */
export function FAQsList({
  faqs,
  tripID,
}: {
  faqs: EditTripFaQsQuery['trip']['faqs']['items'];
  tripID: string;
}) {
  return (
    <div role="list">
      {faqs.map((faq) => (
        <FAQCard faq={faq} key={faq.id} tripID={tripID} />
      ))}
    </div>
  );
}

export function AddNewFAQ({ tripID }: { tripID: string }) {
  const [showNewFAQForm, setShowNewFAQForm] = useState(false);

  if (!showNewFAQForm) {
    return (
      <div className="d-grid gap-2">
        <Button className="mb-2" onClick={() => setShowNewFAQForm(true)}>
          Add FAQ
        </Button>
      </div>
    );
  }

  return (
    <div className="mb-3">
      <FAQEditForm tripID={tripID} setOpen={setShowNewFAQForm} />
    </div>
  );
}

/**
 * Edit Trip FAQS Tab
 */
export default function EditTripFAQs({
  trip,
}: {
  trip: Pick<LegacyTrip, 'id'>;
}) {
  // Get the initial values
  const { data, loading, error } = useQuery(EditTripFaQsDocument, {
    variables: { tripID: trip.id },
    fetchPolicy: 'cache-and-network',
  });

  // Handle loading/error
  if (loading) return <LoadingPage />;
  if (error) return <ErrorPage error={error} />;

  // Return the form
  return (
    <>
      <AddNewFAQ tripID={data.trip.id} />
      <FAQsList faqs={data.trip.faqs.items} tripID={data.trip.id} />
    </>
  );
}
